import { i18n } from '@/i18n';

type ApiErrorPayload = {
  success?: boolean;
  code?: string;
  message?: string;
  error?: string;
  details?: {
    resource?: string;
    used?: number;
    limit?: number;
    resetAt?: string | null;
    [key: string]: unknown;
  };
};

export class ApiClientError extends Error {
  code?: string;
  details?: ApiErrorPayload['details'];
  status?: number;

  constructor(
    message: string,
    options: { code?: string; details?: ApiErrorPayload['details']; status?: number } = {},
  ) {
    super(message);
    this.name = 'ApiClientError';
    this.code = options.code;
    this.details = options.details;
    this.status = options.status;
  }
}

function quotaMessage(payload: ApiErrorPayload) {
  const detail = payload.details;
  const usage = detail && Number.isFinite(detail.used) && Number.isFinite(detail.limit)
    ? i18n.global.t('errors.quotaUsage', { used: detail.used, limit: detail.limit })
    : '';
  const reset = detail?.resetAt
    ? i18n.global.t('errors.quotaReset', { resetAt: new Date(detail.resetAt).toLocaleString() })
    : '';
  return `${payload.message || i18n.global.t('errors.quotaLimitExceeded')}${usage}${reset}${i18n.global.t('errors.quotaAction')}`;
}

function messageFromPayload(payload: ApiErrorPayload | undefined, status?: number, fallback = i18n.global.t('errors.requestFailed')) {
  if (payload?.code === 'QUOTA_LIMIT_EXCEEDED') return quotaMessage(payload);
  if (payload?.code === 'SUBSCRIPTION_READ_ONLY') {
    return payload.message || i18n.global.t('errors.subscriptionReadOnly');
  }
  if (payload?.code === 'SESSION_REVOKED') {
    return payload.message || i18n.global.t('auth.sessionRevoked');
  }
  if (payload?.code === 'EMAIL_ALREADY_REGISTERED' || payload?.code === 'EMAIL_ALREADY_EXISTS') {
    return i18n.global.t('personalAuth.emailAlreadyRegistered');
  }
  if (payload?.error) return payload.error;
  if (payload?.message) return payload.message;
  if (status === 400) return i18n.global.t('errors.badRequest');
  if (status === 401) return i18n.global.t('errors.unauthorized');
  if (status === 403) return i18n.global.t('errors.forbidden');
  if (status === 404) return i18n.global.t('errors.notFound');
  if (status && status >= 500) return i18n.global.t('errors.unavailable');
  return fallback;
}

export function normalizeApiError(error: unknown, fallback = i18n.global.t('errors.requestFailed')): ApiClientError {
  if (error instanceof ApiClientError) return error;

  const candidate = error as {
    message?: string;
    status?: number;
    data?: ApiErrorPayload;
    response?: { status?: number; data?: ApiErrorPayload };
  };
  const status = candidate?.response?.status ?? candidate?.status;
  const payload = candidate?.response?.data ?? candidate?.data;
  const originalMessage = candidate?.message || '';
  const message = payload
    ? messageFromPayload(payload, status, fallback)
    : originalMessage && !/^Request failed with status code \d+$/i.test(originalMessage)
      ? originalMessage
      : messageFromPayload(undefined, status, fallback);

  return new ApiClientError(message, {
    code: payload?.code,
    details: payload?.details,
    status,
  });
}

export function getApiErrorMessage(error: unknown, fallback = i18n.global.t('errors.requestFailed')): string {
  return normalizeApiError(error, fallback).message;
}
